import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Mask } from './Mask.js'
import type {
  PositionYield,
  ZerionPortfolio,
  ZerionPosition,
} from '../types.js'
import type { PortfolioState } from '../hooks/usePortfolio.js'
import type { PositionsState } from '../hooks/usePositions.js'

interface Props {
  state: PortfolioState
  positionsState: PositionsState
  positionsOpen: boolean
  onTogglePositions: () => void
}

/**
 * Always-on portfolio summary for the Main Safe. Renders total USD value,
 * 1d delta, and a stacked bar of chain distribution. Hidden entirely if
 * the worker doesn't have ZERION_API_KEY (503) — we don't want to push
 * a noisy "feature unavailable" card on every dashboard view.
 */
export function PortfolioCard({
  state,
  positionsState,
  positionsOpen,
  onTogglePositions,
}: Props) {
  if (state.status === 'idle') return null
  if (state.status === 'error' && state.isUnavailable) return null

  if (state.status === 'loading') {
    return (
      <Card className="mb-4">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Wallet className="size-4" />
            Loading portfolio…
          </div>
        </CardContent>
      </Card>
    )
  }

  if (state.status === 'error') {
    return (
      <Card className="mb-4">
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <Wallet className="size-4" />
            Portfolio unavailable: {state.message}
          </div>
        </CardContent>
      </Card>
    )
  }

  const { portfolio, refreshedAt } = state
  return (
    <Card className="mb-4">
      <CardContent>
        <PortfolioBody portfolio={portfolio} refreshedAt={refreshedAt} />
        <div className="mt-3 border-t pt-3">
          <button
            type="button"
            onClick={onTogglePositions}
            className="flex w-full items-center justify-between text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="flex items-center gap-1">
              {positionsOpen ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              {positionsOpen ? 'Hide positions' : 'Show positions'}
            </span>
            {positionsState.status === 'ready' && (
              <span className="text-[10px]">
                {positionsState.page.data.length} held
              </span>
            )}
          </button>
          {positionsOpen && (
            <div className="mt-3">
              <PositionsList state={positionsState} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function PortfolioBody({
  portfolio,
  refreshedAt,
}: {
  portfolio: ZerionPortfolio
  refreshedAt: Date
}) {
  const { attributes } = portfolio.data
  const dist = attributes.positions_distribution_by_type
  // Zerion's `total.positions` is net worth, but the aggregate `borrowed`
  // bucket can be polluted by looped-vault internals (Morpho curated vaults
  // that the user perceives as pure lending sometimes carry an underlying
  // borrow that Zerion attributes back to the depositor). DeBank shows the
  // gross asset total instead and that matches user mental model — sum the
  // supply-side categories so the headline reads as "what does this Safe
  // hold?" rather than "what is its leveraged net?".
  const gross = dist.wallet + dist.deposited + dist.staked + dist.locked
  const change1d = attributes.changes.absolute_1d
  const pct1d = attributes.changes.percent_1d
  const chainShares = topChainShares(attributes.positions_distribution_by_chain)

  const typeShares = topTypeShares(attributes.positions_distribution_by_type)
  const isAllWallet =
    typeShares.length === 1 && typeShares[0]!.kind === 'wallet'

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            <Wallet className="size-3" />
            Portfolio total
          </div>
          <div className="mt-0.5 text-3xl font-semibold tabular-nums">
            <Mask as="$***,***">{fmtUsd(gross)}</Mask>
          </div>
        </div>
        <ChangePill absolute={change1d} percent={pct1d} />
      </div>

      {chainShares.length > 0 && (
        <div className="space-y-1.5">
          <ChainBar shares={chainShares} total={gross} />
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {chainShares.map((s) => (
              <span key={s.chain} className="flex items-center gap-1.5">
                <span
                  className="inline-block size-2 rounded-full"
                  style={{ background: chainColor(s.chain) }}
                />
                <span className="text-muted-foreground">
                  {prettyChain(s.chain)}
                </span>
                <span className="font-mono tabular-nums">
                  <Mask as="$***">{fmtUsdCompact(s.value)}</Mask>
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {!isAllWallet && typeShares.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 border-t pt-2 text-xs">
          <span className="text-muted-foreground">By position</span>
          {typeShares.map((s) => (
            <span key={s.kind} className="flex items-center gap-1.5">
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide',
                  TYPE_COLORS[s.kind] ?? 'text-muted-foreground',
                )}
              >
                {s.kind}
              </span>
              <span className="font-mono tabular-nums">
                <Mask as="$***">{fmtUsdCompact(s.value)}</Mask>
              </span>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-muted-foreground">
        <span>as of {refreshedAt.toLocaleTimeString()} · cached ~1h</span>
        <a
          href={`https://debank.com/profile/${portfolio.data.id}`}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1 text-sky-400 hover:underline"
        >
          View Portfolio on DeBank
          <ExternalLink className="size-3" />
        </a>
      </div>
    </div>
  )
}

function ChangePill({
  absolute,
  percent,
}: {
  absolute: number
  percent: number
}) {
  const positive = absolute >= 0
  const Icon = positive ? TrendingUp : TrendingDown
  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium tabular-nums',
        positive
          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
          : 'border-destructive/40 bg-destructive/10 text-destructive',
      )}
      title="24h change"
    >
      <Icon className="size-3" />
      <Mask as="$*** (***%)">
        {positive ? '+' : ''}
        {fmtUsd(absolute)} ({positive ? '+' : ''}
        {(percent * 100).toFixed(2)}%)
      </Mask>
    </div>
  )
}

interface ChainShare {
  chain: string
  value: number
}

type PositionKind = 'wallet' | 'deposited' | 'borrowed' | 'locked' | 'staked'

interface TypeShare {
  kind: PositionKind
  value: number
}

const TYPE_COLORS: Record<PositionKind, string> = {
  wallet: 'text-foreground',
  deposited: 'text-sky-400',
  staked: 'text-emerald-400',
  locked: 'text-amber-400',
  borrowed: 'text-destructive',
}

function topTypeShares(dist: {
  wallet: number
  deposited: number
  borrowed: number
  locked: number
  staked: number
}): TypeShare[] {
  // Borrowed is excluded — Zerion's aggregate often counts vault internals
  // as a borrow even when the user is purely lending. The Borrowed group in
  // the per-position list (only renders for real `loan` positions) is the
  // source of truth for actual debt.
  const positiveTypes: PositionKind[] = ['wallet', 'deposited', 'staked', 'locked']
  return positiveTypes
    .map((kind) => ({ kind, value: dist[kind] }))
    .filter((s) => s.value > 0.01)
    .sort((a, b) => b.value - a.value)
}

function topChainShares(
  dist: Record<string, number>,
  topN = 5,
): ChainShare[] {
  const entries = Object.entries(dist)
    .filter(([, v]) => v > 0)
    .map(([chain, value]) => ({ chain, value }))
    .sort((a, b) => b.value - a.value)
  if (entries.length <= topN) return entries
  const top = entries.slice(0, topN)
  const restSum = entries
    .slice(topN)
    .reduce((acc, s) => acc + s.value, 0)
  return [...top, { chain: 'other', value: restSum }]
}

function ChainBar({ shares }: { shares: ChainShare[]; total: number }) {
  // Divide by the sum of shares (not the headline total) so the bar always
  // fills its container — Zerion's chain distribution sums to net worth,
  // which doesn't match the gross headline when the wallet has debt.
  const sum = shares.reduce((acc, s) => acc + s.value, 0)
  if (sum <= 0) return null
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
      {shares.map((s) => {
        const pct = (s.value / sum) * 100
        return (
          <div
            key={s.chain}
            className="h-full"
            style={{ width: `${pct}%`, background: chainColor(s.chain) }}
            title={`${prettyChain(s.chain)} · ${fmtUsdCompact(s.value)} · ${pct.toFixed(1)}%`}
          />
        )
      })}
    </div>
  )
}

const CHAIN_COLORS: Record<string, string> = {
  ethereum: '#627eea',
  base: '#0052ff',
  arbitrum: '#28a0f0',
  optimism: '#ff0420',
  polygon: '#8247e5',
  'polygon-zkevm': '#7b3fe4',
  'binance-smart-chain': '#f3ba2f',
  avalanche: '#e84142',
  zksync: '#1e69ff',
  scroll: '#ffe4b3',
  linea: '#000000',
  gnosis: '#3e6957',
  fantom: '#1969ff',
  other: '#8b949e',
}

function chainColor(chain: string): string {
  return CHAIN_COLORS[chain] ?? '#8b949e'
}

function prettyChain(chain: string): string {
  if (!chain) return 'Unknown'
  if (chain === 'binance-smart-chain') return 'BNB'
  if (chain === 'polygon-zkevm') return 'Polygon zkEVM'
  return chain[0]!.toUpperCase() + chain.slice(1)
}

function fmtUsd(n: number): string {
  if (Math.abs(n) >= 1) {
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    })
  }
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
}

function fmtUsdCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(0)}`
}

const POSITION_TYPE_LABELS: Record<string, string> = {
  wallet: 'Wallet',
  deposit: 'Deposited',
  loan: 'Borrowed',
  staked: 'Staked',
  locked: 'Locked',
  reward: 'Rewards',
}

const POSITION_TYPE_ORDER = [
  'wallet',
  'staked',
  'deposit',
  'locked',
  'reward',
  'loan',
]

function PositionsList({ state }: { state: PositionsState }) {
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading positions…
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div className="text-xs text-destructive">
        Couldn't load positions: {state.message}
      </div>
    )
  }
  if (state.page.data.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No positions returned for this Safe.
      </div>
    )
  }

  const groups = groupByType(state.page.data)

  return (
    <div className="max-h-96 space-y-3 overflow-auto pr-1">
      {POSITION_TYPE_ORDER.filter((k) => (groups.get(k) ?? []).length > 0).map(
        (kind) => (
          <PositionGroup
            key={kind}
            label={POSITION_TYPE_LABELS[kind] ?? kind}
            positions={groups.get(kind) ?? []}
          />
        ),
      )}
      {Array.from(groups.keys())
        .filter((k) => !POSITION_TYPE_ORDER.includes(k))
        .map((kind) => (
          <PositionGroup
            key={kind}
            label={POSITION_TYPE_LABELS[kind] ?? kind}
            positions={groups.get(kind) ?? []}
          />
        ))}
    </div>
  )
}

function PositionGroup({
  label,
  positions,
}: {
  label: string
  positions: ZerionPosition[]
}) {
  // Subtotal explains why the portfolio total can be lower than the sum of
  // visible asset rows — borrows (Zerion sends loan values as negative)
  // pull the net down. Showing per-group totals lets the user reconcile
  // wallet + deposited + … - borrowed = portfolio total at a glance.
  const subtotal = positions.reduce(
    (acc, p) => (p.attributes.value != null ? acc + p.attributes.value : acc),
    0,
  )
  const negative = subtotal < 0

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        <span
          className={cn('font-mono tabular-nums', negative && 'text-destructive')}
        >
          <Mask as="$***,***">{fmtUsd(subtotal)}</Mask>
        </span>
      </div>
      <ul className="space-y-1.5">
        {positions.map((p) => (
          <PositionRow key={p.id} position={p} />
        ))}
      </ul>
    </div>
  )
}

function PositionRow({ position }: { position: ZerionPosition }) {
  const a = position.attributes
  const sym = a.fungible_info?.symbol ?? '—'
  const name = a.fungible_info?.name ?? a.name
  const chain = position.relationships?.chain?.data?.id ?? null
  const dapp = position.relationships?.dapp?.data?.id ?? null
  const verified = a.fungible_info?.flags?.verified ?? false
  const value = a.value
  const qty = a.quantity.float
  const yieldInfo = trustworthyYield(position)

  return (
    <li className="flex items-center justify-between gap-3 rounded-sm bg-muted/30 px-2 py-1.5 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        {a.fungible_info?.icon?.url ? (
          <img
            src={a.fungible_info.icon.url}
            alt=""
            loading="lazy"
            className="size-5 shrink-0 rounded-full bg-muted object-cover"
          />
        ) : (
          <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-mono uppercase text-muted-foreground">
            {sym.slice(0, 2)}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className="font-medium">{sym}</span>
            <span className="truncate text-xs text-muted-foreground">{name}</span>
            {!verified && a.fungible_info && (
              <span className="text-[9px] uppercase tracking-wide text-amber-400/70">
                unverified
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-baseline gap-1.5 text-[10px] text-muted-foreground">
            {chain && (
              <Badge variant="outline" className="font-normal text-[9px]">
                {prettyChain(chain)}
              </Badge>
            )}
            {dapp && (
              <Badge variant="secondary" className="font-normal text-[9px]">
                {dapp}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="shrink-0 text-right tabular-nums">
        <div className="font-mono text-sm">
          <Mask as="$***,***">{value != null ? fmtUsd(value) : '—'}</Mask>
        </div>
        <div className="text-[10px] text-muted-foreground">
          <Mask as={`*** ${sym}`}>
            {fmtQty(qty)} {sym}
          </Mask>
        </div>
        {yieldInfo && yieldInfo.apy != null && (
          <a
            href={yieldInfo.defillamaUrl}
            target="_blank"
            rel="noopener"
            className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-emerald-400 hover:underline"
            title={`Source: DefiLlama · ${yieldInfo.project}`}
          >
            {yieldInfo.apy.toFixed(2)}% APY
            <ExternalLink className="size-2.5" />
          </a>
        )}
      </div>
    </li>
  )
}

/**
 * DefiLlama matches by symbol can hit false positives — a meme-token can
 * share a name with a yield pool. Trust the match only when:
 *   - APY is positive (zero/null = collateral pool, not yield)
 *   - the position is on a known DeFi protocol (has a `dapp` relationship), OR
 *   - it's a known liquid-staking / synth receipt token in the wallet.
 */
function trustworthyYield(p: ZerionPosition): PositionYield | null {
  const y = p._yield
  if (!y || y.apy == null || y.apy <= 0) return null
  const isWalletOnly =
    p.attributes.position_type === 'wallet' &&
    !p.relationships?.dapp?.data?.id
  if (isWalletOnly) {
    const symbol = (p.attributes.fungible_info?.symbol ?? '').toLowerCase()
    if (!YIELD_BEARING_WALLET_TOKENS.has(symbol)) return null
  }
  return y
}

const YIELD_BEARING_WALLET_TOKENS = new Set([
  // liquid staking
  'weeth',
  'wsteth',
  'steth',
  'cbeth',
  'rsteth',
  'reth',
  'eeth',
  'meth',
  // synthetic dollars
  'susde',
  'usde',
  'sdai',
  'susds',
  'sfrax',
])

function groupByType(positions: ZerionPosition[]): Map<string, ZerionPosition[]> {
  const m = new Map<string, ZerionPosition[]>()
  for (const p of positions) {
    const k = p.attributes.position_type
    const arr = m.get(k) ?? []
    arr.push(p)
    m.set(k, arr)
  }
  return m
}

function fmtQty(n: number): string {
  if (n === 0) return '0'
  // Loan positions can carry negative quantities — operate on the
  // magnitude and re-attach the sign so we don't fall through every
  // `>=` branch and render `-5000 USDC` as `<0.0001`.
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1000) {
    return sign + abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }
  if (abs >= 1) {
    return sign + abs.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }
  if (abs >= 0.0001) {
    return sign + abs.toLocaleString('en-US', { maximumFractionDigits: 4 })
  }
  return sign + '<0.0001'
}
